/**
 * Owns the page's two pieces of changing state — the selected channel and
 * the local-copy probe's answer — and redraws through `render.ts`. Shared
 * by the production entry (`main.ts`) and the developer harness
 * (`harness.ts`), so both draw a verification outcome the same way.
 */
import { findChannelRelease } from './channels'
import { COMMUNITY_SITES, type CommunitySite } from './config'
import type { LocalCopyStatus } from './localCopy'
import {
  browserStorage,
  isLocalCopyRemembered,
  type LocalCopyStorageSource,
  rememberLocalCopy,
} from './localCopyMemory'
import { type LocalCopyDisplay, renderLocalCopy, renderPage } from './render'
import type { VerificationOutcome } from './verify'
import { buildNeutralReleaseUrl } from './neutralGateway'
import { pointReleaseLinks, showVerifiedLinks } from './verifiedLinks'
import type { VersionManifest } from '../../../scripts/ipfs-release/schema'

/** What the verified page needs from outside: the probe, storage, and the community list. */
export interface VerifiedPageOptions {
  /** Resolves once the local server answers or the probe gives up. Must never reject. */
  readonly probeLocalCopy: () => Promise<LocalCopyStatus>
  /** Where a found local copy is remembered. Defaults to `localStorage`. */
  readonly storage?: LocalCopyStorageSource
  /** Defaults to `COMMUNITY_SITES` from `config.ts`. */
  readonly communitySites?: readonly CommunitySite[]
}

/** Who started a probe — it decides what a failed probe shows. */
type ProbeTrigger = 'reader' | 'remembered'

/**
 * What the hero shows after a probe. A failed probe the reader asked for
 * says "not found"; a failed remembered probe shows only the control again,
 * with no error — and the remembered flag stays, for the next visit.
 */
const displayAfterProbe = (status: LocalCopyStatus, trigger: ProbeTrigger): LocalCopyDisplay => {
  if (status.kind === 'running') {
    return status
  }
  if (trigger === 'reader') {
    return { kind: 'not-found' }
  }
  return { kind: 'idle' }
}

/**
 * Draws the verified page on the list's default channel. The page never
 * probes the reader's computer on load unless a local copy answered on an
 * earlier visit — see `localCopyMemory.ts` for why. Otherwise it shows a
 * quiet "Check for my local copy" control, and probes when pressed.
 *
 * The page always draws first and never waits for a probe; when a probe
 * settles, only the hero's local-copy slot changes.
 *
 * @returns a promise that settles after the on-load probe's answer is
 * drawn, or at once when there is no on-load probe — for tests; the page
 * itself never awaits it.
 */
export const showVerifiedPage = (
  root: HTMLElement,
  manifest: VersionManifest,
  options: VerifiedPageOptions,
): Promise<void> => {
  const communitySites = options.communitySites ?? COMMUNITY_SITES
  const storage = options.storage ?? browserStorage
  const remembered = isLocalCopyRemembered(storage)
  let selectedChannel = manifest.defaultChannel
  let localCopy: LocalCopyDisplay = remembered ? { kind: 'checking' } : { kind: 'idle' }

  const redrawLocalCopy = (): void => {
    const shownRelease = findChannelRelease(manifest, selectedChannel)
    if (shownRelease === undefined) {
      return
    }
    renderLocalCopy(root, localCopy, shownRelease, checkLocalCopy)
  }

  const probe = (trigger: ProbeTrigger): Promise<void> => {
    localCopy = { kind: 'checking' }
    redrawLocalCopy()
    return options.probeLocalCopy().then((status) => {
      if (status.kind === 'running') {
        rememberLocalCopy(storage)
      }
      localCopy = displayAfterProbe(status, trigger)
      redrawLocalCopy()
    })
  }

  function checkLocalCopy(): void {
    if (localCopy.kind === 'checking') {
      return
    }
    void probe('reader')
  }

  const draw = (): void => {
    renderPage(root, {
      kind: 'verified',
      manifest,
      selectedChannel,
      localCopy,
      communitySites,
      onSelectChannel: (channel) => {
        selectedChannel = channel
        draw()
      },
      onCheckLocalCopy: checkLocalCopy,
      storage,
    })
  }

  draw()

  if (!remembered) {
    return Promise.resolve()
  }
  return probe('remembered')
}

/**
 * Points the frame's docs links (top bar and footer) into the default
 * channel's release, at its own content-address origin.
 */
const pointFrameLinks = (scope: ParentNode, manifest: VersionManifest): void => {
  const release = findChannelRelease(manifest, manifest.defaultChannel)
  if (release === undefined) {
    return
  }
  pointReleaseLinks(scope, buildNeutralReleaseUrl(manifest, release.cid))
}

/** Draws one verification outcome. A verified list starts the page described at {@link showVerifiedPage}. */
export const showVerificationOutcome = (
  root: HTMLElement,
  outcome: VerificationOutcome,
  options: VerifiedPageOptions,
): void => {
  showVerifiedLinks(root.ownerDocument, { visible: outcome.status === 'verified' })
  switch (outcome.status) {
    case 'verified':
      pointFrameLinks(root.ownerDocument, outcome.manifest)
      void showVerifiedPage(root, outcome.manifest, options)
      return
    case 'verification-failed':
      renderPage(root, { kind: 'verification-failed', reason: outcome.reason })
      return
    case 'unreachable':
      renderPage(root, { kind: 'unreachable' })
      return
    default:
      outcome satisfies never
  }
}
